import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { XMarkIcon, CheckCircleIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import {
  usePendenciaDetalhe,
  useOcorrencias,
  useRegistrarOcorrencia,
  useResolverPendencia,
} from '../../hooks/useConferencias';
import { usePermissoes } from '../../hooks/usePermissoes';
import { CATEGORIAS_PENDENCIA } from '../../types/conferencia';
import GaleriaFotosConferencia from './GaleriaFotosConferencia';
import { useToast } from '../ui/Toast';
import { formatDateTime } from '../../utils/format';
import { btnPrimario, btnSecundario } from '../ui/iris';

/** Chave → rótulo PT das categorias (CATEGORIAS_PENDENCIA é a fonte-da-verdade). */
const LABEL_CAT: Record<string, string> = Object.fromEntries(
  CATEGORIAS_PENDENCIA.map((c) => [c.chave, c.label])
);

/**
 * Dialog deep-linkável de detalhe da pendência — rota filha de `/conferencia/pendencias/:id`
 * (Task 10 monta a rota; este componente compila e funciona isolado). Busca a conferência
 * SOZINHO por `id` de `useParams` via `usePendenciaDetalhe` (NUNCA `lista.find(...)` — um
 * link direto/refresh sem a lista da página-mãe carregada mostraria vazio). Consome o
 * controller de união (Task 6/7) — o mesmo GET que atende quem só tem `conferencia_pendencia`
 * (sem a policy `conferencia_nfe`), inclusive as fotos (`GaleriaFotosConferencia` com
 * `api="pendencia"`, que usa os endpoints `/conferencia-pendencia/...` em vez de
 * `/conferencia-nfe/...` — senão o supervisor tomaria 403 no GET das fotos).
 *
 * Fechar sempre volta pro pai (`/conferencia/pendencias`), preservando os filtros da lista —
 * o dialog é só uma rota filha sobreposta, a página-mãe permanece montada por baixo.
 */
const ConferenciaPendenciaDetalheDialog: React.FC = () => {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const idValido = Number.isFinite(id);
  const navigate = useNavigate();
  const toast = useToast();
  const { temPermissao } = usePermissoes();
  const podeTratar = temPermissao('conferencia_pendencia');

  const { data: c, isLoading, error, refetch } = usePendenciaDetalhe(idValido ? id : undefined);
  const { data: ocorrencias } = useOcorrencias(idValido ? id : undefined);
  const registrar = useRegistrarOcorrencia();
  const resolver = useResolverPendencia();
  const [texto, setTexto] = useState('');

  const fechar = () => navigate('/conferencia/pendencias');

  // ESC fecha + trava o scroll do body enquanto aberto (mesmo molde de ConcluirConferenciaDialog
  // / FotosConferenciaDialog). Roda só uma vez (monta/desmonta) — `fechar` fecha sobre `navigate`,
  // que o React Router garante estável, então recriar o listener a cada render seria só ruído.
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
    window.addEventListener('keydown', onEsc);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = original;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enviarAndamento = () => {
    const t = texto.trim();
    if (!t) return;
    registrar.mutate(
      { id, texto: t },
      {
        onSuccess: () => setTexto(''),
        onError: () => toast.erro('Falha ao registrar o andamento.'),
      }
    );
  };

  const marcarResolvida = () => {
    resolver.mutate(
      { id, body: {} },
      {
        onSuccess: () => toast.sucesso('Pendência resolvida.'),
        onError: () => toast.erro('Falha ao resolver.'),
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50" onClick={fechar}>
      <div className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" />
      <div className="fixed inset-0 flex flex-col items-center justify-center p-0 md:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Detalhe da pendência"
          onClick={(e) => e.stopPropagation()}
          className="relative flex h-full w-full max-w-2xl flex-col overflow-hidden bg-surface md:h-auto md:max-h-[90vh] md:rounded-iris-lg md:border md:border-line-strong md:shadow-iris-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
            <h2 className="truncate text-base font-semibold text-tx">
              {c
                ? `NF-e ${c.numeroNfe ?? id}${c.serieNfe ? `/${c.serieNfe}` : ''} — ${c.fornecedorNome ?? 'fornecedor não informado'}`
                : 'Pendência'}
            </h2>
            <button
              type="button"
              onClick={fechar}
              aria-label="Fechar"
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-iris text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx md:h-8 md:w-8"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Corpo (scroll) */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {!idValido && (
              <p className="rounded-iris border border-danger-border bg-danger-soft p-3 text-sm text-danger">
                Identificador de pendência inválido.
              </p>
            )}

            {idValido && isLoading && <p className="text-sm text-tx-3">Carregando…</p>}

            {idValido && !isLoading && error && (
              <div className="rounded-iris border border-danger-border bg-danger-soft p-3 text-sm text-danger">
                <p>Não foi possível carregar a pendência.</p>
                <button type="button" onClick={() => refetch()} className={`${btnSecundario} mt-2`}>
                  Tentar novamente
                </button>
              </div>
            )}

            {c && !c.temPendencia && (
              <p className="rounded-iris bg-surface-2 p-3 text-sm text-tx-2">
                Esta nota não tem pendência declarada.
              </p>
            )}

            {c && c.temPendencia && (
              <>
                {/* Categorias */}
                <div className="flex flex-wrap gap-1.5">
                  {(c.pendenciaCategorias ?? '').split(',').filter(Boolean).map((chave) => (
                    <span
                      key={chave}
                      className="rounded-full border border-warning-border bg-warning-soft px-2 py-0.5 text-[11px] font-medium text-warning"
                    >
                      {LABEL_CAT[chave] ?? chave}
                    </span>
                  ))}
                </div>

                {/* Descrição declarada na conclusão */}
                {c.pendenciaObservacao && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-tx-3">Descrição</p>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-tx">{c.pendenciaObservacao}</p>
                  </div>
                )}

                {/* Fotos — reúso via GaleriaFotosConferencia (Task 8), api="pendencia" pro
                    supervisor só-conferencia_pendencia não tomar 403 no GET de fotos. */}
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-tx-3">Fotos</p>
                  <GaleriaFotosConferencia conferenciaId={id} api="pendencia" />
                </div>

                {/* Linha do tempo */}
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-tx-3">Linha do tempo</p>
                  <ul className="mt-1 space-y-2">
                    {(ocorrencias ?? []).map((o) => (
                      <li
                        key={o.id}
                        className={`rounded-iris border px-3 py-2 text-sm ${
                          o.tipo === 'manual' ? 'border-line bg-surface' : 'border-line bg-surface-2 text-tx-2'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 text-[11px] text-tx-3">
                          <span>{o.usuario}{o.tipo !== 'manual' ? ' · sistema' : ''}</span>
                          <span className="tabular-nums">{formatDateTime(o.criadoEm)}</span>
                        </div>
                        <p className="mt-0.5 whitespace-pre-wrap text-tx">{o.texto}</p>
                      </li>
                    ))}
                    {(ocorrencias ?? []).length === 0 && (
                      <li className="text-sm text-tx-3">Sem andamentos ainda.</li>
                    )}
                  </ul>

                  {/* Registrar andamento — colaboração: qualquer um com acesso à rota registra,
                      não exige conferencia_pendencia (só "Marcar como resolvida" é gated). */}
                  <div className="mt-2 flex items-end gap-2">
                    <textarea
                      value={texto}
                      onChange={(e) => setTexto(e.target.value)}
                      rows={2}
                      maxLength={2000}
                      placeholder="Registrar um andamento…"
                      className="min-h-11 flex-1 resize-none rounded-iris-sm border border-line-strong bg-surface-2 px-3 py-2 text-sm text-tx placeholder:text-tx-3 focus:border-accent focus:bg-surface focus:outline-none focus:ring-[3px] focus:ring-accent-soft"
                    />
                    <button
                      type="button"
                      onClick={enviarAndamento}
                      disabled={registrar.isPending || !texto.trim()}
                      className={`${btnPrimario} h-11`}
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                      Registrar
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer: resolver (só quem trata; só quando ainda aberta) */}
          {c && c.temPendencia && !c.pendenciaResolvida && (
            <div className="flex flex-col-reverse gap-2 border-t border-line bg-surface-2 px-4 py-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={fechar} className={`${btnSecundario} w-full sm:w-auto`}>
                Fechar
              </button>
              {podeTratar && (
                <button
                  type="button"
                  onClick={marcarResolvida}
                  disabled={resolver.isPending}
                  className={`${btnPrimario} w-full sm:w-auto`}
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  {resolver.isPending ? 'Salvando…' : 'Marcar como resolvida'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConferenciaPendenciaDetalheDialog;
